import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import {Check} from 'lucide-react';
import {cn} from '../../lib/cn';
import {Label} from '../../label';
import {ErrorMessage} from '../../error-message';
import {AirbnbFieldHelperText} from '../field-helper-text';

export interface AirbnbSwitchProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
  'onChange' | 'value'
> {
  value?: boolean;
  onChange?: React.ComponentPropsWithoutRef<
    typeof SwitchPrimitives.Root
  >['onCheckedChange'];
  loading?: boolean;
  readOnly?: boolean;
  label?: React.ReactNode;
  error?: string;
  helper?: React.ReactNode;
  wrapperClassName?: string;
}

export const AirbnbSwitch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  AirbnbSwitchProps
>(
  (
    {
      className,
      value,
      onChange,
      disabled,
      loading,
      readOnly,
      id,
      label,
      error,
      helper,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const fieldId = id || generatedId;
    const helperId = `${fieldId}-helper`;

    const switchElement = (
      <SwitchPrimitives.Root
        ref={ref}
        aria-describedby={helper ? helperId : undefined}
        className={cn(
          'group inline-flex h-[24px] w-[36px] shrink-0 cursor-pointer items-center rounded-full border border-solid border-transparent p-[2px] transition-colors duration-200',
          'focus-visible:outline-none focus-visible:shadow-[var(--chekin-shadow-focus)]',
          'disabled:cursor-not-allowed disabled:opacity-50 aria-busy:cursor-wait aria-busy:opacity-50',
          'aria-readonly:cursor-default aria-readonly:opacity-100',
          'data-[state=checked]:bg-[var(--chekin-airbnb-ink-strong)] data-[state=unchecked]:bg-[var(--chekin-airbnb-border-soft)]',
          className,
        )}
        id={fieldId}
        disabled={disabled}
        checked={value}
        value={String(value)}
        onCheckedChange={!disabled && !readOnly ? onChange : undefined}
        aria-busy={loading}
        aria-readonly={readOnly}
        {...props}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'flex h-[20px] w-[20px] items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.22)] transition-transform duration-200',
            'data-[state=checked]:translate-x-[12px] data-[state=unchecked]:translate-x-0',
          )}
        >
          <Check
            aria-hidden="true"
            className="h-3 w-3 text-[var(--chekin-airbnb-ink-strong)] opacity-0 transition-opacity duration-150 group-data-[state=checked]:opacity-100"
            strokeWidth={3}
          />
        </SwitchPrimitives.Thumb>
      </SwitchPrimitives.Root>
    );

    if (!label && !error && !helper) {
      return switchElement;
    }

    return (
      <>
        <div className={cn('flex items-center gap-x-3 gap-y-1.5', wrapperClassName)}>
          {switchElement}
          {label && (
            <Label
              htmlFor={fieldId}
              className={cn(
                'text-base font-medium',
                readOnly ? 'cursor-default' : 'cursor-pointer',
              )}
            >
              {label}
            </Label>
          )}
        </div>
        {error && <ErrorMessage disabled={disabled}>{error}</ErrorMessage>}
        {!error && (
          <AirbnbFieldHelperText id={helperId} helper={helper} disabled={disabled} />
        )}
      </>
    );
  },
);

AirbnbSwitch.displayName = 'AirbnbSwitch';
